if (!customElements.get('pluscup-product')) {
  customElements.define('pluscup-product', class PluscupProduct extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized === 'true') return;
      this.dataset.initialized = 'true';
      this.form = this.querySelector('[data-pc-form]');
      if (!this.form) return;
      this.variantInput = this.querySelector('[data-pc-variant-id]');
      this.submitButton = this.querySelector('[data-pc-submit]');
      this.formError = this.querySelector('[data-pc-form-error]');
      this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';
      this.price = Number(this.dataset.price || 0);
      this.comparePrice = Number(this.dataset.comparePrice || 0);
      this.available = this.dataset.available === 'true';
      this.bindGallery();
      this.bindVariants();
      this.bindFields();
      this.bindUploads();
      this.bindAddons();
      this.bindNotices();
      this.bindModals();
      this.bindSubmit();
      this.bindStickyButton();
      this.updateTotal();
    }

    formatMoney(cents) {
      const amount = (Number(cents || 0) / 100);
      const token = (this.moneyFormat.match(/\{\{\s*(\w+)\s*\}\}/) || [])[1] || 'amount';
      const formatNumber = (value, decimals, thousands, decimal) => {
        const fixed = value.toFixed(decimals).split('.');
        fixed[0] = fixed[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
        return fixed[0] + (decimals ? decimal + fixed[1] : '');
      };
      let value;
      if (token === 'amount_no_decimals') value = formatNumber(amount, 0, ',', '.');
      else if (token === 'amount_with_comma_separator') value = formatNumber(amount, 2, '.', ',');
      else if (token === 'amount_no_decimals_with_comma_separator') value = formatNumber(amount, 0, '.', ',');
      else if (token === 'amount_with_apostrophe_separator') value = formatNumber(amount, 2, "'", '.');
      else value = formatNumber(amount, 2, ',', '.');
      return this.moneyFormat.replace(/\{\{\s*\w+\s*\}\}/, value);
    }

    bindGallery() {
      const main = this.querySelector('[data-pc-main-image]');
      this.querySelectorAll('[data-pc-thumb]').forEach((button) => {
        button.addEventListener('click', () => {
          this.querySelectorAll('[data-pc-thumb]').forEach((item) => item.setAttribute('aria-current', 'false'));
          button.setAttribute('aria-current', 'true');
          if (main) {
            main.src = button.dataset.full;
            main.alt = button.dataset.alt || '';
          }
        });
      });
    }

    bindVariants() {
      this.querySelectorAll('[data-pc-variant]').forEach((button) => {
        button.addEventListener('click', () => {
          if (button.disabled) return;
          this.querySelectorAll('[data-pc-variant]').forEach((item) => item.setAttribute('aria-pressed', 'false'));
          button.setAttribute('aria-pressed', 'true');
          this.variantInput.value = button.dataset.id;
          this.price = Number(button.dataset.price || 0);
          this.comparePrice = Number(button.dataset.comparePrice || 0);
          this.available = button.dataset.available === 'true';
          const url = new URL(window.location.href);
          url.searchParams.set('variant', button.dataset.id);
          window.history.replaceState({}, '', url);
          this.updateTotal();
        });
      });
    }

    bindFields() {
      this.querySelectorAll('[data-pc-field]').forEach((field) => {
        const update = () => {
          field.classList.remove('is-invalid');
          const error = field.closest('[data-pc-field-wrap]')?.querySelector('[data-pc-field-error]');
          if (error) error.hidden = true;
          const counter = field.closest('[data-pc-field-wrap]')?.querySelector('[data-pc-counter]');
          if (counter) counter.textContent = field.value.length;
        };
        field.addEventListener('input', update);
        field.addEventListener('change', update);
      });
    }

    bindUploads() {
      this.querySelectorAll('[data-pc-upload]').forEach((input) => {
        const wrap = input.closest('[data-pc-upload-wrap]');
        const preview = wrap.querySelector('[data-pc-file-preview]');
        const name = wrap.querySelector('[data-pc-file-name]');
        const remove = wrap.querySelector('[data-pc-file-remove]');
        const refresh = () => {
          const file = input.files?.[0];
          input.classList.remove('is-invalid');
          wrap.querySelector('[data-pc-field-error]').hidden = true;
          if (!file) {
            preview.hidden = true;
            preview.removeAttribute('src');
            name.textContent = wrap.dataset.emptyText || 'Choose an image';
            remove.hidden = true;
            this.updateTotal();
            return;
          }
          const limitMb = Number(input.dataset.maxMb || 10);
          if (file.size > limitMb * 1024 * 1024) {
            input.value = '';
            input.classList.add('is-invalid');
            const error = wrap.querySelector('[data-pc-field-error]');
            error.textContent = `Please choose a file under ${limitMb} MB.`;
            error.hidden = false;
            preview.hidden = true;
            preview.removeAttribute('src');
            name.textContent = wrap.dataset.emptyText || 'Choose an image';
            remove.hidden = true;
            this.updateTotal();
            return;
          }
          name.textContent = file.name;
          remove.hidden = false;
          if (file.type.startsWith('image/')) {
            const reader = new FileReader();
            reader.onload = (event) => { preview.src = event.target.result; preview.hidden = false; };
            reader.readAsDataURL(file);
          }
          this.updateTotal();
        };
        input.addEventListener('change', refresh);
        remove.addEventListener('click', (event) => { event.preventDefault(); input.value = ''; refresh(); });
      });
    }

    bindAddons() {
      this.querySelectorAll('[data-pc-addon-check]').forEach((checkbox) => {
        checkbox.addEventListener('change', () => {
          const wrap = checkbox.closest('[data-pc-addon]');
          const subfields = wrap?.querySelector('[data-pc-subfields]');
          if (subfields) subfields.hidden = !checkbox.checked;
          this.updateTotal();
        });
      });
    }

    bindNotices() {
      this.querySelectorAll('[data-pc-notice]').forEach((notice) => {
        notice.querySelector('[data-pc-notice-close]')?.addEventListener('click', () => { notice.hidden = true; });
      });
    }

    bindModals() {
      this.querySelectorAll('[data-pc-modal-open]').forEach((button) => {
        button.addEventListener('click', () => {
          const modal = this.querySelector(`#${CSS.escape(button.dataset.pcModalOpen)}`);
          if (!modal) return;
          const template = modal.querySelector('template');
          const slot = modal.querySelector('[data-pc-video-slot]');
          if (template && slot && !slot.hasChildNodes()) slot.appendChild(template.content.cloneNode(true));
          modal.classList.add('is-open');
          modal.setAttribute('aria-hidden', 'false');
          document.body.style.overflow = 'hidden';
          modal.querySelector('[data-pc-modal-close]')?.focus();
        });
      });
      this.querySelectorAll('[data-pc-modal]').forEach((modal) => {
        const close = () => {
          modal.classList.remove('is-open');
          modal.setAttribute('aria-hidden', 'true');
          modal.querySelector('[data-pc-video-slot]')?.replaceChildren();
          document.body.style.overflow = '';
        };
        modal.querySelector('[data-pc-modal-close]')?.addEventListener('click', close);
        modal.addEventListener('click', (event) => { if (event.target === modal) close(); });
        modal.addEventListener('keydown', (event) => { if (event.key === 'Escape') close(); });
      });
    }

    bindSubmit() {
      this.form.addEventListener('submit', async (event) => {
        event.preventDefault();
        if (!this.available || this.submitButton.disabled) return;
        const properties = this.collectProperties();
        if (!properties) return;
        const key = `pc-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
        properties._PlusCupKey = key;
        const oldLabel = this.submitButton.innerHTML;
        this.formError.hidden = true;
        this.submitButton.disabled = true;
        this.submitButton.textContent = this.submitButton.dataset.loadingText || 'Adding…';
        try {
          const upload = this.querySelector('[data-pc-upload][data-main-upload="true"]');
          await this.addItem(this.variantInput.value, properties, upload?.files?.[0]);
          for (const addon of this.selectedAddons(key)) await this.addItem(addon.id, addon.properties, null);
          window.location.assign(`${window.Shopify.routes.root}cart`);
        } catch (error) {
          console.error(error);
          this.formError.textContent = this.formError.dataset.errorText || 'Something went wrong. Please try again.';
          this.formError.hidden = false;
          this.submitButton.disabled = false;
          this.submitButton.innerHTML = oldLabel;
        }
      });
    }

    collectProperties() {
      let valid = true;
      let firstInvalid;
      const properties = {};
      this.querySelectorAll('[data-pc-field]').forEach((field) => {
        const value = String(field.value || '').trim();
        if ((field.dataset.required === 'true' && !value) || (value && !field.checkValidity())) {
          valid = false;
          firstInvalid ||= field;
          field.classList.add('is-invalid');
          const error = field.closest('[data-pc-field-wrap]')?.querySelector('[data-pc-field-error]');
          if (error) error.hidden = false;
        }
        if (value) properties[field.dataset.property] = value;
      });
      this.querySelectorAll('[data-pc-upload]').forEach((field) => {
        const file = field.files?.[0];
        if (field.dataset.required === 'true' && !file) {
          valid = false;
          firstInvalid ||= field;
          field.classList.add('is-invalid');
          const error = field.closest('[data-pc-upload-wrap]')?.querySelector('[data-pc-field-error]');
          if (error) error.hidden = false;
        }
      });
      this.querySelectorAll('[data-pc-addon-check]:checked').forEach((checkbox) => {
        const wrap = checkbox.closest('[data-pc-addon]');
        wrap?.querySelectorAll('[data-pc-addon-field]').forEach((field) => {
          const value = String(field.value || '').trim();
          if (field.dataset.required === 'true' && !value) {
            valid = false;
            firstInvalid ||= field;
            field.classList.add('is-invalid');
          }
        });
      });
      if (!valid) {
        firstInvalid?.focus();
        this.formError.textContent = this.formError.dataset.requiredText || 'Please complete the required fields.';
        this.formError.hidden = false;
        return null;
      }
      return properties;
    }

    selectedAddons(key) {
      const addons = [];
      this.querySelectorAll('[data-pc-addon]').forEach((wrap) => {
        const checkbox = wrap.querySelector('[data-pc-addon-check]');
        if (!checkbox?.checked || !wrap.dataset.variantId) return;
        const properties = { _PlusCupKey: key };
        if (wrap.dataset.property) properties[wrap.dataset.property] = wrap.dataset.value || 'Yes';
        wrap.querySelectorAll('[data-pc-addon-field]').forEach((field) => {
          const value = String(field.value || '').trim();
          if (value) properties[field.dataset.property] = value;
        });
        addons.push({ id: wrap.dataset.variantId, properties });
      });
      this.querySelectorAll('[data-pc-upload]').forEach((input) => {
        const file = input.files?.[0];
        if (!file || !input.dataset.addonVariant) return;
        addons.push({ id: input.dataset.addonVariant, properties: { _PlusCupKey: key, [input.dataset.property]: file.name } });
      });
      return addons;
    }

    async addItem(id, properties, file) {
      let body;
      const options = { method: 'POST', headers: { Accept: 'application/json' } };
      if (file) {
        body = new FormData();
        body.append('id', id);
        body.append('quantity', '1');
        Object.entries(properties).forEach(([key, value]) => body.append(`properties[${key}]`, value));
        const propertyName = this.querySelector('[data-pc-upload][data-main-upload="true"]')?.dataset.property || 'Photo';
        body.append(`properties[${propertyName}]`, file);
      } else {
        options.headers['Content-Type'] = 'application/json';
        body = JSON.stringify({ id, quantity: 1, properties });
      }
      options.body = body;
      const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, options);
      if (!response.ok) {
        const result = await response.json().catch(() => ({}));
        throw new Error(result.description || 'Unable to add item');
      }
      return response.json();
    }

    addonsTotal() {
      let total = 0;
      this.querySelectorAll('[data-pc-addon-check]:checked').forEach((checkbox) => {
        total += Number(checkbox.closest('[data-pc-addon]')?.dataset.priceCents || 0);
      });
      this.querySelectorAll('[data-pc-upload]').forEach((input) => {
        if (input.files?.length) total += Number(input.dataset.priceCents || 0);
      });
      return total;
    }

    updateTotal() {
      const addons = this.addonsTotal();
      const total = this.price + addons;
      const totalElement = this.querySelector('[data-pc-total]');
      const totalCompare = this.querySelector('[data-pc-total-compare]');
      const priceElement = this.querySelector('[data-pc-price]');
      const compareElement = this.querySelector('[data-pc-compare]');
      if (priceElement) {
        priceElement.textContent = this.formatMoney(this.price);
        priceElement.classList.toggle('is-sale', this.comparePrice > this.price);
      }
      if (compareElement) {
        compareElement.hidden = !(this.comparePrice > this.price);
        compareElement.textContent = this.formatMoney(this.comparePrice);
      }
      if (totalElement) totalElement.textContent = this.formatMoney(total);
      if (totalCompare) {
        totalCompare.hidden = !(this.comparePrice > this.price);
        totalCompare.textContent = this.formatMoney(this.comparePrice + addons);
      }
      if (this.submitButton) {
        this.submitButton.disabled = !this.available;
        this.submitButton.textContent = this.available ? this.submitButton.dataset.availableText : this.submitButton.dataset.soldOutText;
      }
    }

    bindStickyButton() {
      const sticky = this.querySelector('[data-pc-sticky]');
      const stickyButton = this.querySelector('[data-pc-sticky-button]');
      if (!sticky || !stickyButton || !this.submitButton) return;
      stickyButton.addEventListener('click', () => this.submitButton.scrollIntoView({ behavior: 'smooth', block: 'center' }));
      if ('IntersectionObserver' in window) {
        new IntersectionObserver((entries) => sticky.classList.toggle('is-visible', !entries[0].isIntersecting), { rootMargin: '0px 0px -70px' }).observe(this.submitButton);
      }
    }
  });
}
